import { IScopeClause, IScopeTopicWithClauses } from "@/api";
import { getClauseHighlightColor } from "@/config/constants";
import { Accordion, AccordionDetails, Box, Stack } from "@mui/material";
import { PageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { useEffect, useMemo, useState } from "react";
import { TopicAccordionSummary } from "./TopicAccordionSummary";
import { IGroupedV2Clause } from "./types";
import { V2ClauseAccordion } from "./V2ClauseAccordion";

interface TopicAccordionProps {
    topic: IScopeTopicWithClauses;
    expanded: boolean;
    onToggle: () => void;
    pageNavigationPluginInstance: PageNavigationPlugin;
    /** The scope_clause.id to highlight (unique per topic) */
    highlightedScopeClauseId?: number | null;
    onClauseSelect?: (scopeClauseId: number | null) => void;
    /** Total unique clause count across all filtered topics (for auto-expand single result) */
    totalUniqueClauseCount?: number;
}

/**
 * Single topic accordion component
 * Uses MUI Accordion with disableGutters and elevation={0}
 * Renders clauses as nested accordions with expand/collapse behavior
 */
export const TopicAccordion = ({
    topic,
    expanded,
    onToggle,
    pageNavigationPluginInstance,
    highlightedScopeClauseId,
    onClauseSelect,
    totalUniqueClauseCount,
}: TopicAccordionProps) => {
    // Track which clause accordion is expanded (null = all collapsed)
    const [expandedClauseId, setExpandedClauseId] = useState<number | null>(
        null
    );

    // Flatten all clauses from sections, group by clause_id, sort alphabetically
    const flattenedClauses = useMemo(() => {
        // Track all unique sections per clause
        const sectionsByClause = new Map<number, Set<string>>();
        const groups = new Map<number, IGroupedV2Clause>();

        topic.sections.forEach((section) => {
            const sectionName = section.section_type?.name || "General";
            section.clauses.forEach((scopeClause: IScopeClause) => {
                const clauseId = scopeClause.clause.id;

                // Track unique sections for this clause
                if (!sectionsByClause.has(clauseId)) {
                    sectionsByClause.set(clauseId, new Set());
                }
                sectionsByClause.get(clauseId)!.add(sectionName);

                if (!groups.has(clauseId)) {
                    groups.set(clauseId, {
                        clause: scopeClause.clause,
                        scopeClauses: [],
                        highlightColor: getClauseHighlightColor(clauseId),
                        sectionNames: [], // Will be populated after loop
                    });
                }

                groups.get(clauseId)!.scopeClauses.push(scopeClause);
            });
        });

        // Populate sectionNames array for each clause
        groups.forEach((group, clauseId) => {
            const sections = sectionsByClause.get(clauseId);
            if (sections) {
                group.sectionNames = Array.from(sections).sort();
            }
        });

        // Sort alphabetically by clause title
        return Array.from(groups.values()).sort((a, b) =>
            a.clause.title.localeCompare(b.clause.title)
        );
    }, [topic.sections]);

    // Auto-expand highlighted clause, collapse when highlight moves elsewhere
    useEffect(() => {
        if (highlightedScopeClauseId) {
            // Find which clause group contains the highlighted scope clause
            const clauseGroup = flattenedClauses.find((group) =>
                group.scopeClauses.some(
                    (sc) => sc.id === highlightedScopeClauseId
                )
            );
            if (clauseGroup) {
                // Highlight is in this topic - expand that clause
                setExpandedClauseId(clauseGroup.clause.id);
            } else {
                // Highlight moved to a different topic - collapse any expanded clause
                setExpandedClauseId(null);
            }
        }
    }, [highlightedScopeClauseId, flattenedClauses]);

    // Auto-expand single clause when search results in exactly one clause
    useEffect(() => {
        if (totalUniqueClauseCount === 1 && flattenedClauses.length === 1) {
            const clauseGroup = flattenedClauses[0];
            setExpandedClauseId(clauseGroup.clause.id);

            // Select the first scope clause and navigate to its page
            const primaryScopeClause = clauseGroup.scopeClauses[0];
            onClauseSelect?.(primaryScopeClause.id);

            if (primaryScopeClause.highlight_areas.length > 0) {
                pageNavigationPluginInstance.jumpToPage(
                    primaryScopeClause.highlight_areas[0].page_index
                );
            }
        }
    }, [
        totalUniqueClauseCount,
        flattenedClauses,
        onClauseSelect,
        pageNavigationPluginInstance,
    ]);

    // Handle clause accordion toggle
    const handleClauseToggle = (clauseGroup: IGroupedV2Clause) => {
        const clauseId = clauseGroup.clause.id;
        const isExpanding = expandedClauseId !== clauseId;

        // Toggle expanded state
        setExpandedClauseId(isExpanding ? clauseId : null);

        // When expanding, select the first scope clause and navigate to its page
        if (isExpanding) {
            const primaryScopeClause = clauseGroup.scopeClauses[0];

            // Select the clause at the first location
            onClauseSelect?.(primaryScopeClause.id);

            // Navigate to PDF page
            if (primaryScopeClause.highlight_areas.length > 0) {
                pageNavigationPluginInstance.jumpToPage(
                    primaryScopeClause.highlight_areas[0].page_index
                );
            }
        } else {
            // Clear selection when collapsing
            onClauseSelect?.(null);
        }
    };

    return (
        <Accordion
            id={`topic-${topic.id}`}
            disableGutters
            elevation={0}
            expanded={expanded}
            onChange={onToggle}
        >
            <TopicAccordionSummary topic={topic} />
            <AccordionDetails
                sx={{ bgcolor: "background.default", py: 1.5, px: 0 }}
            >
                <Box
                    sx={{
                        borderTop: 1,
                        borderBottom: 1,
                        borderColor: "divider",
                        overflow: "hidden",
                        bgcolor: "background.paper",
                    }}
                >
                    <Stack
                        gap={0}
                        divider={
                            <Box
                                sx={{ borderBottom: 1, borderColor: "divider" }}
                            />
                        }
                    >
                        {flattenedClauses.map((clauseGroup) => {
                            // Find highlighted scope clause (if any)
                            const highlightedScopeClause =
                                clauseGroup.scopeClauses.find(
                                    (sc) => sc.id === highlightedScopeClauseId
                                );
                            const isHighlighted = !!highlightedScopeClause;
                            const isClauseExpanded =
                                expandedClauseId === clauseGroup.clause.id;

                            return (
                                <V2ClauseAccordion
                                    key={clauseGroup.clause.id}
                                    clauseGroup={clauseGroup}
                                    expanded={isClauseExpanded}
                                    onToggle={() =>
                                        handleClauseToggle(clauseGroup)
                                    }
                                    pageNavigationPluginInstance={
                                        pageNavigationPluginInstance
                                    }
                                    isHighlighted={isHighlighted}
                                    highlightedScopeClauseId={
                                        highlightedScopeClause?.id
                                    }
                                    onClauseSelect={onClauseSelect}
                                />
                            );
                        })}
                    </Stack>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};
